import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, Plus } from 'lucide-react';
import ManualBlockModal from './ManualBlockModal';
import { JobDetailModal } from './JobDetailModal';

const AvailabilityCalendar = ({ isAdmin = false, selectedUserId = null }) => {
  const [currentWeekStart, setCurrentWeekStart] = useState(getWeekStart(new Date()));
  const [availability, setAvailability] = useState(null);
  const [blockedSlots, setBlockedSlots] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(selectedUserId || '');
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [showJobModal, setShowJobModal] = useState(false);
  const [showManualBlockModal, setShowManualBlockModal] = useState(false);

  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');

  // Generate time slots (30-minute increments from 7 AM to 7 PM)
  const timeSlots = [];
  for (let hour = 7; hour <= 19; hour++) {
    for (let minute = 0; minute < 60; minute += 30) {
      if (hour === 19 && minute > 0) break; // Stop at 7:00 PM
      timeSlots.push(`${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`);
    }
  }

  // Get week days
  const weekDays = [];
  for (let i = 0; i < 7; i++) {
    const date = new Date(currentWeekStart);
    date.setDate(date.getDate() + i);
    weekDays.push(date);
  }

  useEffect(() => {
    if (isAdmin) {
      fetchUsers();
    }
  }, [isAdmin]);

  useEffect(() => {
    const userId = isAdmin ? selectedUser : currentUser.id;
    if (userId) {
      fetchAvailability(userId);
      fetchAppointments(userId);
    }
  }, [selectedUser, currentWeekStart, isAdmin]);

  function getWeekStart(date) {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day; // Sunday as start
    return new Date(d.setDate(diff));
  }

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/admin/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data.filter(u => !u.is_admin));
        if (!selectedUser && data.length > 0) {
          setSelectedUser(data.find(u => !u.is_admin)?.id || '');
        }
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchAvailability = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      const endpoint = isAdmin ? `/api/availability/${userId}` : '/api/availability/my';
      const response = await fetch(`${backendUrl}${endpoint}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAvailability(data);
        setBlockedSlots(data.blocked_slots || []);
      } else {
        console.error(`Failed to fetch availability: ${response.status} ${response.statusText}`);
        const errorText = await response.text();
        console.error('Error details:', errorText);
      }
    } catch (error) {
      console.error('Error fetching availability:', error);
    }
  };

  const fetchAppointments = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/users/${userId}/appointments`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAppointments(data);
      }
    } catch (error) {
      console.error('Error fetching appointments:', error);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const userId = isAdmin ? selectedUser : currentUser.id;
      const endpoint = isAdmin ? `/api/availability/${userId}` : '/api/availability/my';
      
      const response = await fetch(`${backendUrl}${endpoint}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          blocked_slots: blockedSlots,
          days_off: availability?.days_off || []
        })
      });

      if (response.ok) {
        alert('✅ Availability saved successfully!');
        fetchAvailability(userId);
      } else {
        alert('Failed to save availability');
      }
    } catch (error) {
      console.error('Error saving availability:', error);
      alert('Failed to save availability');
    } finally {
      setSaving(false);
    }
  };

  const handleManualBlockSave = (blocks) => {
    // Add the new blocks to existing blocked slots
    // Remove duplicates
    const existingBlocksSet = new Set(
      blockedSlots.map(slot => `${slot.date}-${slot.start}`)
    );
    
    const newBlocks = blocks.filter(
      block => !existingBlocksSet.has(`${block.date}-${block.start}`)
    );
    
    setBlockedSlots([...blockedSlots, ...newBlocks]);
    setShowManualBlockModal(false);
    alert(`✅ Added ${newBlocks.length} time block(s). Click [SAVE CHANGES] to apply.`);
  };

  const toggleSlot = (date, time) => {
    const dateStr = formatDate(date);
    const endTime = getEndTime(time);
    
    // Check if slot is already blocked
    const existingIndex = blockedSlots.findIndex(
      slot => slot.date === dateStr && slot.start === time
    );

    if (existingIndex >= 0) {
      // Remove block
      setBlockedSlots(blockedSlots.filter((_, i) => i !== existingIndex));
    } else {
      // Add block
      setBlockedSlots([...blockedSlots, {
        date: dateStr,
        start: time,
        end: endTime,
        reason: 'Blocked'
      }]);
    }
  };

  const toggleDayOff = (date) => {
    const dateStr = formatDate(date);
    const daysOff = availability?.days_off || [];
    
    if (daysOff.includes(dateStr)) {
      setAvailability({
        ...availability,
        days_off: daysOff.filter(d => d !== dateStr)
      });
    } else {
      setAvailability({
        ...availability,
        days_off: [...daysOff, dateStr]
      });
    }
  };

  const getSlotStatus = (date, time) => {
    const dateStr = formatDate(date);
    const endTime = getEndTime(time);
    
    // Check if entire day is off
    if (availability?.days_off?.includes(dateStr)) {
      return { status: 'day-off', appointment: null };
    }

    // Check if outside working hours
    if (availability) {
      const workStart = availability.work_start_time || '07:00';
      const workEnd = availability.work_end_time || '19:00';
      if (time < workStart || time >= workEnd) {
        return { status: 'outside-hours', appointment: null };
      }
    }

    // Check if blocked
    const isBlocked = blockedSlots.some(
      slot => slot.date === dateStr && slot.start === time
    );
    if (isBlocked) return { status: 'blocked', appointment: null };

    // Check if booked with appointment
    const bookedAppointment = appointments.find(apt => {
      if (apt.appointment_date !== dateStr) return false;
      if (apt.status === 'Cancelled' || apt.status === 'Closed') return false;
      const aptStart = apt.start_time;
      const aptEnd = apt.end_time;
      return time >= aptStart && time < aptEnd;
    });
    if (bookedAppointment) return { status: 'booked', appointment: bookedAppointment };

    return { status: 'available', appointment: null };
  };

  const getSlotColor = (status) => {
    switch (status) {
      case 'available':
        return 'bg-green-600 hover:bg-green-700 cursor-pointer';
      case 'blocked':
        return 'bg-gray-800 hover:bg-gray-700 cursor-pointer';
      case 'booked':
        return 'bg-blue-600 hover:bg-blue-700 cursor-pointer';
      case 'day-off':
        return 'bg-red-800 cursor-not-allowed';
      case 'outside-hours':
        return 'bg-black/50 cursor-not-allowed';
      default:
        return 'bg-gray-600';
    }
  };

  const handleSlotClick = (date, time, slotInfo) => {
    if (slotInfo.status === 'booked' && slotInfo.appointment) {
      // Open job details
      setSelectedAppointment(slotInfo.appointment);
      setShowJobModal(true);
    } else if (slotInfo.status === 'available' || slotInfo.status === 'blocked') {
      // Toggle availability
      toggleSlot(date, time);
    }
  };

  function formatDate(date) {
    // Format date in local timezone to avoid UTC conversion issues
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  function getEndTime(startTime) {
    const [hours, minutes] = startTime.split(':').map(Number);
    const endMinutes = minutes + 30;
    if (endMinutes >= 60) {
      return `${(hours + 1).toString().padStart(2, '0')}:${(endMinutes - 60).toString().padStart(2, '0')}`;
    }
    return `${hours.toString().padStart(2, '0')}:${endMinutes.toString().padStart(2, '0')}`;
  }

  const previousWeek = () => {
    const newDate = new Date(currentWeekStart);
    newDate.setDate(newDate.getDate() - 7);
    setCurrentWeekStart(newDate);
  };

  const nextWeek = () => {
    const newDate = new Date(currentWeekStart);
    newDate.setDate(newDate.getDate() + 7);
    setCurrentWeekStart(newDate);
  };

  if (!availability) {
    return <div data-testid="availability-loading" className="p-6 text-[var(--accent)]">Loading availability calendar...</div>;
  }

  return (
    <div className="p-4">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        <h2 className="text-2xl font-bold text-[var(--accent)]">
          {isAdmin ? 'TECHNICIAN AVAILABILITY' : 'MY AVAILABILITY'}
        </h2>
        <div className="flex gap-2">
          <Button 
            onClick={() => setShowManualBlockModal(true)}
            data-testid="availability-block-time"
            className="bg-black border-2 border-white hover:bg-gray-900 text-white font-bold"
          >
            <Plus size={20} /> [BLOCK TIME]
          </Button>
          <Button 
            onClick={handleSave} 
            data-testid="availability-save"
            disabled={saving}
            className="bg-black border-2 border-white hover:bg-gray-900 text-white font-bold"
          >
            {saving ? 'SAVING...' : '[SAVE CHANGES]'}
          </Button>
        </div>
      </div>

      {/* User Selection (Admin) */}
      {isAdmin && (
        <div className="mb-4">
          <select
            data-testid="availability-technician-select"
            value={selectedUser}
            onChange={(e) => setSelectedUser(e.target.value)}
            className="w-full px-4 py-2 bg-black border-2 border-[var(--accent)] text-[var(--accent)]"
          >
            <option value="">-- Select Technician --</option>
            {users.map(user => (
              <option key={user.id} value={user.id}>{user.username}</option>
            ))}
          </select>
        </div>
      )}

      {/* Week Navigation */}
      <div className="flex flex-col md:flex-row items-center justify-between gap-4 mb-4">
        <Button 
          onClick={previousWeek} 
          data-testid="availability-previous-week"
          className="bg-black border-2 border-white hover:bg-gray-900 text-white font-bold w-full md:w-auto"
        >
          <ChevronLeft size={20} /> [PREVIOUS WEEK]
        </Button>
        
        <div className="text-center">
          <div className="text-white font-bold text-lg">
            {weekDays[0].toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - 
            {weekDays[6].toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
          </div>
          <div className="flex gap-2 mt-2 justify-center">
            <select
              data-testid="availability-month-select"
              value={currentWeekStart.getMonth()}
              onChange={(e) => {
                const newDate = new Date(currentWeekStart);
                newDate.setMonth(parseInt(e.target.value));
                setCurrentWeekStart(getWeekStart(newDate));
              }}
              className="px-3 py-1 bg-black border-2 border-white text-white text-sm"
            >
              {['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].map((month, idx) => (
                <option key={idx} value={idx}>{month}</option>
              ))}
            </select>
            <select
              data-testid="availability-year-select"
              value={currentWeekStart.getFullYear()}
              onChange={(e) => {
                const newDate = new Date(currentWeekStart);
                newDate.setFullYear(parseInt(e.target.value));
                setCurrentWeekStart(getWeekStart(newDate));
              }}
              className="px-3 py-1 bg-black border-2 border-white text-white text-sm"
            >
              {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 1 + i).map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
            <Button
              onClick={() => setCurrentWeekStart(getWeekStart(new Date()))}
              data-testid="availability-today"
              className="bg-black border-2 border-white hover:bg-gray-900 text-white text-sm font-bold px-3 py-1"
            >
              [TODAY]
            </Button>
          </div>
        </div>
        
        <Button 
          onClick={nextWeek} 
          data-testid="availability-next-week"
          className="bg-black border-2 border-white hover:bg-gray-900 text-white font-bold w-full md:w-auto"
        >
          [NEXT WEEK] <ChevronRight size={20} />
        </Button>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-4 mb-4 text-sm">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 bg-green-600 border border-white"></div>
          <span className="text-white">Available (Click to block)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 bg-gray-800 border border-white"></div>
          <span className="text-white">Blocked (Click to unblock)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 bg-blue-600 border border-white"></div>
          <span className="text-white">Booked (Click to view job)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 bg-red-800 border border-white"></div>
          <span className="text-[var(--accent)]">Day Off</span>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="overflow-x-auto">
        <div className="min-w-[800px]">
          {/* Header Row - Days */}
          <div className="grid grid-cols-8 gap-1 mb-2">
            <div className="text-[var(--accent)] font-bold text-center p-2">Time</div>
            {weekDays.map((date, idx) => (
              <div key={idx} className="text-center">
                <div className="text-[var(--accent)] font-bold">
                  {date.toLocaleDateString('en-US', { weekday: 'short' })}
                </div>
                <div className="text-[var(--accent)]/70 text-sm">
                  {date.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' })}
                </div>
                <button
                  onClick={() => toggleDayOff(date)}
                  data-testid={`availability-day-off-${formatDate(date)}`}
                  className="text-xs text-[var(--accent)] hover:text-red-500 mt-1"
                >
                  {availability?.days_off?.includes(formatDate(date)) ? '[Remove Day Off]' : '[Mark Day Off]'}
                </button>
              </div>
            ))}
          </div>

          {/* Time Slots */}
          {timeSlots.map((time) => (
            <div key={time} className="grid grid-cols-8 gap-1 mb-1">
              <div className="text-[var(--accent)] text-sm p-2 text-right">
                {time}
              </div>
              {weekDays.map((date, idx) => {
                const slotInfo = getSlotStatus(date, time);
                const colorClass = getSlotColor(slotInfo.status);

                return (
                  <div
                    key={idx}
                    data-testid={`availability-slot-${formatDate(date)}-${time.replace(':', '-')}`}
                    data-status={slotInfo.status}
                    onClick={() => handleSlotClick(date, time, slotInfo)}
                    className={`h-8 ${colorClass} border border-[var(--accent)]/30 flex items-center justify-center text-xs text-white transition-all`}
                    title={slotInfo.status === 'booked' && slotInfo.appointment ? 
                      `${slotInfo.appointment.customer_name} - ${slotInfo.appointment.job_type}` : 
                      slotInfo.status}
                  >
                    {slotInfo.status === 'booked' && '📅'}
                    {slotInfo.status === 'day-off' && '🚫'}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {/* Manual Block Modal */}
      {showManualBlockModal && (
        <ManualBlockModal
          onClose={() => setShowManualBlockModal(false)}
          onSave={handleManualBlockSave}
          currentWeekStart={currentWeekStart}
        />
      )}

      {/* Job Details Modal */}
      {showJobModal && selectedAppointment && (
        <JobDetailModal appointment={selectedAppointment} onClose={() => setShowJobModal(false)}
          onUpdate={() => fetchAppointments(isAdmin ? selectedUser : currentUser.id)} />
      )}
    </div>
  );
};

export default AvailabilityCalendar;
